import { isAxiosError } from "axios";

export const PARTNER_SERVER_ERROR = "PARTNER_SERVER_ERROR";
export const PARTNER_COMPANY_NOT_FOUND = "PARTNER_COMPANY_NOT_FOUND";
export const PARTNER_USER_NOT_FOUND = "PARTNER_USER_NOT_FOUND";
export const PARTNER_USER_WITH_MAIL_EXISTS = "PARTNER_USER_WITH_MAIL_EXISTS";
export const PARTNER_MULTIPLE_USERS_FOR_COMPANY =
  "PARTNER_MULTIPLE_USERS_FOR_COMPANY";
export const PARTNER_CREATION_FAILED = "PARTNER_CREATION_FAILED";
export const PARTNER_UPDATE_FAILED = "PARTNER_UPDATE_FAILED";
export const PARTNER_INVALID_REQUEST = "PARTNER_INVALID_REQUEST";
export const PARTNER_INVALID_TENANT = "PARTNER_INVALID_TENANT";
export const PARTNER_TENANT_REASSIGNMENT = "PARTNER_TENANT_REASSIGNMENT";
export const MARKET_ID_NOT_UNIQUE = "MARKET_ID_NOT_UNIQUE";
export const ORDER_NOTIFICATION_EMAILS_REQUIRED_FOR_PRODUCER =
  "ORDER_NOTIFICATION_EMAILS_REQUIRED_FOR_PRODUCER";
export const PRODUCTS_COULD_NOT_UPDATE = "PRODUCTS_COULD_NOT_UPDATE";
export const PRODUCTS_COULD_NOT_DELETE = "PRODUCTS_COULD_NOT_DELETE";
export const PRODUCTS_NO_COMPANY = "PRODUCTS_NO_COMPANY";
export const PRODUCTS_INVALID_REQUEST = "PRODUCTS_INVALID_REQUEST";
export const USER_CREATION_FAILED = "USER_CREATION_FAILED";
export const USER_WITH_MAIL_EXISTS = "USER_WITH_MAIL_EXISTS";
export const USER_UPDATE_FAILED = "USER_UPDATE_FAILED";
export const USER_SERVER_ERROR = "USER_SERVER_ERROR";
export const USER_ARCHIVED = "USER_ARCHIVED";
export const USER_MULTI_COMPANY = "USER_MULTI_COMPANY";
export const MAGIC_LOGIN_LINK_GENERATION_FAILED =
  "MAGIC_LOGIN_LINK_GENERATION_FAILED";
export const RELATION_COMPANY_IDS_DONT_MATCH =
  "RELATION_COMPANY_IDS_DONT_MATCH";
export const RELATION_SERVER_ERROR = "RELATION_SERVER_ERROR";
export const RELATION_RELATION_DOES_NOT_EXIST =
  "RELATION_RELATION_DOES_NOT_EXIST";
export const RELATION_CHAT_EXISTS = "RELATION_CHAT_EXISTS";
export const RELATION_EXISTS = "RELATION_EXISTS";
export const RELATION_UPDATE_DATA_INVALID = "RELATION_UPDATE_DATA_INVALID";
export const USERS_EXIST = "USERS_EXIST";
export const SERVER_ERROR = "SERVER_ERROR";
export const UNKNOWN_ERROR = "UNKNOWN_ERROR";

const defaultErrorMap = {
  [SERVER_ERROR]:
    "Ein Serverfehler ist aufgetreten. Bitte versuche es später noch einmal.",
  [UNKNOWN_ERROR]: "Ein unbekannter Fehler ist aufgetreten.",
};

const errorMap = {
  [PARTNER_SERVER_ERROR]: defaultErrorMap[SERVER_ERROR],
  [PARTNER_COMPANY_NOT_FOUND]:
    "Zum gewünschten Partner konnte keine Firma gefunden werden.",
  [PARTNER_USER_NOT_FOUND]:
    "Zum gewünschten Partner konnte kein Benutzer gefunden werden.",
  [PARTNER_MULTIPLE_USERS_FOR_COMPANY]:
    "Zum gewünschten Partner wurde mehr als ein Benutzer gefunden.",
  [PARTNER_CREATION_FAILED]: "Der Partner konnte nicht angelegt werden.",
  [PARTNER_UPDATE_FAILED]: "Der Partner konnte nicht aktualisiert werden.",
  [PARTNER_USER_WITH_MAIL_EXISTS]:
    "Ein Benutzer mit dieser E-Mail existiert schon.",
  [PARTNER_INVALID_REQUEST]:
    "Der Partner konnte wegen fehlerhafter Eingaben nicht gespeichert werden.",
  [PARTNER_INVALID_TENANT]: "Bitte wähle einen gültigen Tenant aus der Liste.",
  [PARTNER_TENANT_REASSIGNMENT]:
    "Der Tenant einer Firma kann nach dem Anlegen nicht mehr geändert werden.",
  [MARKET_ID_NOT_UNIQUE]:
    "Diese Marktnummer ist bereits vergeben. Bitte prüfe die Eingabe oder wende dich an einen Administrator.",
  [ORDER_NOTIFICATION_EMAILS_REQUIRED_FOR_PRODUCER]:
    "E-Mail-Adressen für Bestellbestätigungen ist für Produzenten erforderlich.",
  [PRODUCTS_NO_COMPANY]:
    "Die Produkte konnten nicht angelegt werden, da die Company nicht gefunden wurde.",
  [PRODUCTS_COULD_NOT_UPDATE]:
    "Die Produkte konnten nicht aktualisiert werden.",
  [PRODUCTS_COULD_NOT_DELETE]: "Die Produkte konnten nicht gelöscht werden.",
  [USER_CREATION_FAILED]: "Der Benutzer konnte nicht angelegt werden.",
  [USER_WITH_MAIL_EXISTS]: "Ein Benutzer mit dieser E-Mail existiert schon.",
  [USER_UPDATE_FAILED]: "Der Benutzer konnte nicht aktualisiert werden.",
  [USER_SERVER_ERROR]: defaultErrorMap[SERVER_ERROR],
  [USER_ARCHIVED]: "Die Aktion ist für archivierte Benutzer nicht verfügbar.",
  [USER_MULTI_COMPANY]:
    "Die Aktion ist für Multi-Company-User nicht verfügbar.",
  [MAGIC_LOGIN_LINK_GENERATION_FAILED]:
    "Der Login-Link konnte nicht generiert werden. Bitte versuche es später noch einmal.",
  [RELATION_COMPANY_IDS_DONT_MATCH]:
    "Die Anfrage war fehlerhaft. Bitte versuche es noch einmal.",
  [RELATION_RELATION_DOES_NOT_EXIST]: "Die gewählte Beziehung existiert nicht.",
  [RELATION_CHAT_EXISTS]:
    "Die gewählte Beziehung wurde nicht gelöscht, weil die betroffenen Partner bereits gechattet haben.",
  [RELATION_EXISTS]:
    "Der Partner konnte nicht gelöscht werden, weil er noch Beziehungen hat.",
  [RELATION_UPDATE_DATA_INVALID]:
    "Die Beziehung konnte nicht aktualisiert werden. Die übermittelten Daten sind nicht gültig.",
  [USERS_EXIST]:
    "Der Partner konnte nicht gelöscht werden, da für den Partner Nutzer existieren. Der Partner kann daher nur archiviert werden.",
  [RELATION_SERVER_ERROR]: defaultErrorMap[SERVER_ERROR],
  ...defaultErrorMap,
};

type ErrorCode = keyof typeof errorMap;

type ErrorResponse = {
  code: string;
};

function isErrorCode(errorCode: string): errorCode is ErrorCode {
  return Boolean(errorMap[errorCode as ErrorCode]);
}

export function isErrorResponse(error: unknown): error is ErrorResponse {
  if (typeof error !== "object" || error === null || error === undefined) {
    return false;
  }
  return (error as ErrorResponse).code != undefined;
}

function mapError(errorCode: string) {
  return isErrorCode(errorCode)
    ? errorMap[errorCode]
    : `${errorMap[UNKNOWN_ERROR]} Error: ${errorCode}`;
}

export const unknownError = mapError(UNKNOWN_ERROR);

export function mapErrorOrElse(errorOrCode: unknown, fallback: string) {
  if (typeof errorOrCode === "string") {
    return mapError(errorOrCode);
  }

  if (isErrorResponse(errorOrCode)) {
    return mapError(errorOrCode.code);
  }

  return fallback;
}

export function mapErrorOrDefault(errorOrCode: unknown) {
  return mapErrorOrElse(errorOrCode, unknownError);
}

export function mapErrorOrDefaultEx(e: unknown) {
  if (isNetworkIssue(e)) {
    return "Die Aktion konnte aufgrund von Server oder Netzwerkproblemen nicht ausgeführt werden.";
  }
  if (isAxiosError(e)) {
    return mapErrorOrElse(e.response?.data, unknownError);
  }
  if (e instanceof Error) {
    return e.message;
  }
  return unknownError;
}

function isNetworkIssue(e: unknown) {
  return isAxiosError(e) && !e.response;
}
